import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Link, useNavigate, useSearchParams} from 'react-router-dom';
import clsx from 'clsx';
import {Navbar} from '@common/ui/navigation/navbar/navbar';
import {Footer} from '@common/ui/footer/footer';
import {Trans} from '@common/i18n/trans';
import {StaticPageTitle} from '@common/seo/static-page-title';
import {FullPageLoader} from '@common/ui/progress/full-page-loader';
import {FormattedDate} from '@common/i18n/formatted-date';
import {ChevronRightIcon} from '@common/icons/material/ChevronRight';
import {useBlogSlugFromUrl} from '@app/blog/use-blog-slug-from-url';
import {useHydratePublicBlogCache} from '@app/blog/use-hydrate-public-blog-cache';
import {
  BlogArticle,
  getPublicBlogLoader,
  usePublicBlogArticle,
  usePublicBlogCatalog,
} from '@app/blog/use-public-blog';
import {BLOG_LOCALES, BlogLocale} from '@app/admin/blog/blog';
import {BlogArticleToc} from '@app/blog/blog-article-toc';
import {BlogHeading} from '@app/blog/use-public-blog';
import {prepareBlogArticleHtml} from '@app/blog/extract-blog-headings';
import {NotFoundPage} from '@common/ui/not-found-page/not-found-page';
import './public-blog.css';

export function PublicBlogLayout() {
  useHydratePublicBlogCache();
  const loader = getPublicBlogLoader();
  const isPreview = !!loader?.isPreview;
  const urlSlug = useBlogSlugFromUrl();
  const slug = isPreview ? loader?.article?.slug ?? '' : urlSlug;
  const posts = loader?.catalog?.posts;
  const unknownPost =
    !isPreview &&
    Boolean(slug) &&
    Array.isArray(posts) &&
    !posts.some(post => post.slug === slug);

  if (unknownPost) {
    return <NotFoundPage />;
  }

  return (
    <div className="flex min-h-screen flex-col bg">
      {isPreview ? <BlogPreviewBanner /> : null}
      <Navbar
        menuPosition="homepage-navbar"
        className="sticky top-0 z-40 flex-shrink-0"
      />
      <main className="flex-auto">
        {slug ? (
          isPreview ? (
            <BlogPreviewArticleView />
          ) : (
            <BlogArticleView slug={slug} />
          )
        ) : (
          <BlogCatalogView />
        )}
      </main>
      <Footer className="mx-14 md:mx-40" />
    </div>
  );
}

function BlogPreviewBanner() {
  return (
    <div className="sticky top-0 z-50 w-full border-b border-divider bg px-16 py-10 text-center text-sm text-main shadow-sm">
      <Trans message="This is a preview. The article is not published yet." />
    </div>
  );
}

function BlogCatalogView() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get('category');
  const catalogQuery = usePublicBlogCatalog();

  const posts = useMemo(() => {
    const all = catalogQuery.data?.posts ?? [];
    if (!activeCategory) return all;
    return all.filter(post => post.category === activeCategory);
  }, [catalogQuery.data, activeCategory]);

  const categories = catalogQuery.data?.categories ?? [];
  const activeCategoryTitle = useMemo(() => {
    if (!activeCategory) {
      return null;
    }
    return (
      categories.find(category => category.slug === activeCategory)?.title ??
      null
    );
  }, [activeCategory, categories]);

  const pageHeading = activeCategoryTitle ?? null;

  const selectCategory = useCallback(
    (slug: string | null) => {
      const next = new URLSearchParams(searchParams);
      if (slug) {
        next.set('category', slug);
      } else {
        next.delete('category');
      }
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  return (
    <div className="max-w-1200 mx-auto w-full px-16 py-48 md:px-24">
      <StaticPageTitle>
        {pageHeading ?? <Trans message="Blog YPage" />}
      </StaticPageTitle>
      <header className="mb-40">
        <h1 className="blog-hero-title">
          {pageHeading ?? <Trans message="Blog YPage" />}
        </h1>
      </header>

      {categories.length > 0 && (
        <div className="mb-32 flex flex-wrap gap-8">
          <CategoryPill
            active={!activeCategory}
            onClick={() => selectCategory(null)}
          >
            <Trans message="All" />
          </CategoryPill>
          {categories.map(category => (
            <CategoryPill
              key={category.slug}
              active={activeCategory === category.slug}
              onClick={() => selectCategory(category.slug)}
            >
              {category.title}
            </CategoryPill>
          ))}
        </div>
      )}

      {catalogQuery.isLoading ? (
        <FullPageLoader />
      ) : posts.length === 0 ? (
        <p className="py-40 text-center text-muted">
          <Trans message="No posts yet" />
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-32 sm:grid-cols-2 lg:grid-cols-3">
          {posts.map(post => (
            <Link
              key={post.id}
              to={`/blog/${post.slug}`}
              className="blog-card group"
            >
              <div className="blog-card-media">
                {post.cover_image ? (
                  <img src={post.cover_image} alt="" loading="lazy" />
                ) : (
                  <div className="blog-card-media-empty" />
                )}
              </div>
              <div className="blog-card-body">
                {post.category_title && (
                  <span className="blog-card-category">
                    {post.category_title}
                  </span>
                )}
                <h2 className="blog-card-title">{post.title}</h2>
                {post.description && (
                  <p className="blog-card-excerpt">{post.description}</p>
                )}
                {post.published_at && (
                  <time className="blog-card-date">
                    <FormattedDate date={post.published_at} />
                  </time>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

interface CategoryPillProps {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

function CategoryPill({active, onClick, children}: CategoryPillProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'rounded-full border px-16 py-6 text-sm transition-colors',
        active
          ? 'border-primary bg-primary text-on-primary'
          : 'border-divider text-muted hover:text-main',
      )}
    >
      {children}
    </button>
  );
}

function BlogPreviewArticleView() {
  const loader = getPublicBlogLoader();
  const previewArticles = loader?.previewArticles ?? {};
  const availableLocales = BLOG_LOCALES.filter(
    locale => previewArticles[locale],
  );
  const [activeLocale, setActiveLocale] = useState<BlogLocale>(
    () => (loader?.locale as BlogLocale) ?? availableLocales[0] ?? 'ru',
  );

  const article = previewArticles[activeLocale] ?? loader?.article ?? null;

  if (!article) {
    return (
      <div className="blog-article-container py-48">
        <p className="text-muted">
          <Trans message="Preview expired" />
        </p>
      </div>
    );
  }

  return (
    <BlogArticleContent
      article={article}
      localeSwitcher={
        availableLocales.length > 1 ? (
          <div className="mb-24 flex gap-4 border-b">
            {availableLocales.map(locale => (
              <button
                key={locale}
                type="button"
                className={clsx(
                  'border-b-2 px-16 py-8 text-sm font-medium uppercase',
                  activeLocale === locale
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted hover:text-main',
                )}
                onClick={() => setActiveLocale(locale)}
              >
                {locale}
              </button>
            ))}
          </div>
        ) : null
      }
    />
  );
}

function BlogArticleView({slug}: {slug: string}) {
  const articleQuery = usePublicBlogArticle(slug);
  const article = articleQuery.data;

  if (articleQuery.isLoading && !article) {
    return <FullPageLoader />;
  }

  if (!article) {
    return <NotFoundPage />;
  }

  return <BlogArticleContent article={article} />;
}

interface BlogArticleContentProps {
  article: BlogArticle;
  localeSwitcher?: React.ReactNode;
}

function resolveBlogArticleContent(article: BlogArticle): {
  html: string;
  headings: BlogHeading[];
} {
  if (article.headings !== undefined) {
    return {html: article.html, headings: article.headings};
  }

  return prepareBlogArticleHtml(article.html);
}

function BlogArticleContent({
  article,
  localeSwitcher,
}: BlogArticleContentProps) {
  const navigate = useNavigate();

  const {html: articleHtml, headings} = useMemo(
    () => resolveBlogArticleContent(article),
    [article],
  );

  useEffect(() => {
    const hash = window.location.hash.slice(1);
    if (!hash) {
      return;
    }
    requestAnimationFrame(() => {
      document.getElementById(hash)?.scrollIntoView({block: 'start'});
    });
  }, [article.slug, articleHtml]);

  const handleArticleClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      const anchor = (e.target as HTMLElement).closest('a');
      if (!anchor) return;
      const href = anchor.getAttribute('href');
      if (!href || !href.startsWith('/blog')) return;
      e.preventDefault();
      navigate(href);
    },
    [navigate],
  );

  return (
    <article className="blog-article">
      <StaticPageTitle>{article.page_title}</StaticPageTitle>
      <div className="blog-article-container py-40">
        <div className="blog-article-header">
          <nav className="blog-article-breadcrumbs" aria-label="Breadcrumbs">
            <Link to="/blog" className="blog-article-breadcrumb-link">
              <Trans message="Blog" />
            </Link>
            {article.category && article.category_title ? (
              <>
                <ChevronRightIcon
                  size="sm"
                  className="blog-article-breadcrumb-sep"
                />
                <Link
                  to={`/blog?category=${encodeURIComponent(article.category)}`}
                  className="blog-article-breadcrumb-link"
                >
                  {article.category_title}
                </Link>
              </>
            ) : null}
          </nav>
          {localeSwitcher}
          <h1 className="blog-article-title">{article.title}</h1>
          {article.published_at && (
            <time className="blog-article-date">
              <FormattedDate date={article.published_at} />
            </time>
          )}
        </div>
        <div
          className={
            headings.length > 0 ? 'blog-article-layout' : 'blog-article-content'
          }
        >
          <div
            className={
              headings.length > 0
                ? 'blog-article-main'
                : 'blog-article-content-main'
            }
          >
            {article.cover_image && (
              <div className="blog-article-cover">
                <img src={article.cover_image} alt="" />
              </div>
            )}
            <div
              className="md-html blog-article-body prose mt-32 dark:prose-invert"
              dangerouslySetInnerHTML={{__html: articleHtml}}
              onClick={handleArticleClick}
            />
          </div>
          {headings.length > 0 ? (
            <aside className="blog-article-toc-aside">
              <div className="blog-article-toc-sticky">
                <BlogArticleToc headings={headings} />
              </div>
            </aside>
          ) : null}
        </div>
      </div>
    </article>
  );
}
